<template>
  <v-form v-if="data" :readonly="readonly" ref="form">
    <v-card class="mb-3" elevation="1">
      <v-card-title>Вопросы для комиссии</v-card-title>
      <v-card-text>
        <v-switch
          v-if="editMode"
          v-model="data.hasQuestions"
          label="Есть ли вопросы к комиссии?"
          color="primary"
          hide-details
          class="mt-0"
          @change="doChange(true)"
        />
        <div v-else class="mb-2">
          Есть ли вопросы к комиссии:
          <span class="font-weight-medium">{{
            data.hasQuestions ? 'Да' : 'Нет'
          }}</span>
        </div>

        <template v-if="data.hasQuestions && data.participants.length">
          <div class="d-flex align-center commission-participant-tabs mt-4">
            <v-tabs
              v-model="activeParticipant"
              show-arrows
              @change="onParticipantChange"
            >
              <v-tab
                v-for="(participant, pIndex) in data.participants"
                :key="pIndex"
                class="commission-participant-tab"
              >
                <span class="commission-participant-tab__label">
                  {{ participantTabLabel(participant, pIndex) }}
                </span>
                <span
                  v-if="editMode && data.participants.length > 1"
                  class="commission-participant-tab__close-wrap"
                  @mousedown.stop.prevent
                  @click.stop
                >
                  <btn-with-confirm
                    icon
                    x-small
                    class="commission-participant-tab__close"
                    color="grey darken-1"
                    icon-main="mdi-close"
                    icon-x-small
                    title="Удалить участника"
                    @click.stop="removeParticipant(pIndex)"
                    @mousedown.native.stop.prevent
                  />
                </span>
              </v-tab>
            </v-tabs>
            <v-btn
              v-if="editMode"
              color="accent"
              fab
              x-small
              class="ml-1 commission-participant-tabs__add"
              title="Добавить участника"
              @click="addParticipant"
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </div>

          <v-tabs-items v-model="activeParticipant" class="mt-2">
            <v-tab-item
              v-for="(participant, pIndex) in data.participants"
              :key="pIndex"
            >
              <v-card outlined class="commission-participant-panel">
                <v-card-text class="pb-2">
                  <v-text-field
                    v-if="editMode"
                    v-model="participant.title"
                    dense
                    hide-details
                    label="Участник"
                    class="commission-participant-title mb-3"
                    @input="doChange()"
                    @blur="doBlur()"
                  />
                  <div
                    v-else
                    class="text-subtitle-2 font-weight-medium mb-3"
                  >
                    {{
                      participant.title ||
                        `Участник ${pIndex + 1}`
                    }}
                  </div>

                  <div
                    v-for="(question, qIndex) in participant.questions"
                    :key="qIndex"
                    class="mb-3"
                  >
                    <div class="d-flex align-center mb-1">
                      <div class="text-caption font-weight-medium">
                        Вопрос {{ qIndex + 1 }}.
                      </div>
                      <v-spacer />
                      <btn-with-confirm
                        v-if="editMode && participant.questions.length > 1"
                        icon
                        x-small
                        class="commission-question-tab__close"
                        color="grey darken-1"
                        icon-main="mdi-close"
                        icon-x-small
                        title="Удалить вопрос"
                        @click.stop="removeQuestion(pIndex, qIndex)"
                      />
                    </div>
                    <v-textarea
                      v-model="question.text"
                      :readonly="readonly"
                      :outlined="editMode"
                      rows="4"
                      auto-grow
                      no-resize
                      hide-details
                      @input="doChange()"
                      @blur="doBlur()"
                    />
                  </div>
                  <div v-if="editMode" class="text-center">
                    <v-btn
                      color="accent"
                      fab
                      x-small
                      title="Добавить вопрос"
                      @click="addQuestion(pIndex)"
                    >
                      <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                  </div>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </template>
      </v-card-text>
    </v-card>
  </v-form>
</template>

<script>
import { copyObject } from '@/lib/objects'
import BtnWithConfirm from '@/components/buttons/btnWithConfirm'
import {
  DEFAULT_COMMISSION_PARTICIPANT,
  DEFAULT_COMMISSION_QUESTION,
  EX_COMMISSION_QUESTIONS_TEMPLATE,
  normalizeCommissionQuestions,
  participantTabLabel,
} from './const'

export default {
  name: 'ExpCommissionQuestions',
  components: { BtnWithConfirm },
  props: {
    value: { type: Object, default: null },
    readonly: { type: Boolean, default: false },
    onCreate: { type: Function, default: null },
    logView: { type: Boolean, default: false },
  },
  data: () => ({
    data: null,
    activeParticipant: 0,
  }),
  computed: {
    editMode() {
      return this.data && !this.readonly && !this.logView
    },
  },
  watch: {
    value: 'reInit',
    readonly: 'reInit',
  },
  async created() {
    if (this.readonly) {
      this.data = normalizeCommissionQuestions(this.value)
    } else if (this.value) {
      this.data = normalizeCommissionQuestions(copyObject(this.value))
    } else {
      this.data = null
    }

    if (!this.data && this.onCreate) {
      this.data = normalizeCommissionQuestions(
        await this.onCreate(copyObject(EX_COMMISSION_QUESTIONS_TEMPLATE()))
      )
    }

    this.clampActiveParticipant()
  },
  mounted() {
    this.reInit()
  },
  methods: {
    participantTabLabel,
    reInit() {
      if (this.readonly) {
        this.data = normalizeCommissionQuestions(this.value)
      } else if (this.value) {
        this.data = normalizeCommissionQuestions(copyObject(this.value))
      }
      this.clampActiveParticipant()
      if (!this.readonly) {
        this.$nextTick().then(() => this.$refs.form?.validate())
      }
    },
    clampActiveParticipant() {
      const len = this.data?.participants?.length || 0
      if (this.activeParticipant >= len) {
        this.activeParticipant = Math.max(0, len - 1)
      }
    },
    onParticipantChange() {
      this.doBlur()
    },
    doChange(force = false) {
      if (!this.readonly) this.$emit('change', this.data, force)
    },
    doBlur() {
      this.$emit('blur')
    },
    addParticipant() {
      this.data.participants.push(DEFAULT_COMMISSION_PARTICIPANT(''))
      this.activeParticipant = this.data.participants.length - 1
      this.doChange(true)
    },
    removeParticipant(index) {
      const idx =
        typeof index === 'number' ? index : this.activeParticipant
      this.data.participants.splice(idx, 1)
      this.clampActiveParticipant()
      this.doChange(true)
    },
    addQuestion(participantIndex) {
      const pIdx =
        typeof participantIndex === 'number'
          ? participantIndex
          : this.activeParticipant
      this.data.participants[pIdx].questions.push(
        DEFAULT_COMMISSION_QUESTION()
      )
      this.doChange(true)
    },
    removeQuestion(participantIndex, questionIndex) {
      const pIdx =
        typeof participantIndex === 'number'
          ? participantIndex
          : this.activeParticipant
      this.data.participants[pIdx].questions.splice(questionIndex, 1)
      this.doChange(true)
    },
  },
}
</script>

<style scoped>
.commission-participant-tabs {
  width: fit-content;
  max-width: 100%;
}
.commission-participant-tabs >>> .v-tabs {
  flex: 0 1 auto;
  width: auto;
}
.commission-participant-tabs__add {
  flex-shrink: 0;
}
.commission-participant-tab {
  display: inline-flex !important;
  align-items: center;
  gap: 2px;
  text-transform: none;
  letter-spacing: normal;
  min-width: 0;
  max-width: 220px;
}
.commission-participant-tab >>> .v-btn {
  margin-right: -4px;
}
.commission-participant-tab__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 160px;
}
.commission-participant-tab__close-wrap {
  display: inline-flex;
  flex-shrink: 0;
}
.commission-participant-tab__close,
.commission-question-tab__close {
  flex-shrink: 0;
  opacity: 0.65;
}
.commission-participant-tab:hover .commission-participant-tab__close,
.commission-participant-tab.v-tab--active .commission-participant-tab__close {
  opacity: 1;
}
.commission-participant-title >>> .v-input__slot {
  background: transparent !important;
}
</style>
